import { useState, useEffect } from 'react';
import { subscribeToAllFirmDocuments } from '../services/firestoreService';
import { TaxDocument, UserRole } from '../types';
import { useAuth } from '../context/AuthContext';

export const useStaffAllDocuments = () => {
  const { user, userDoc, firmId } = useAuth();
  const [documents, setDocuments] = useState<TaxDocument[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || !userDoc || !firmId) {
      if (loading) setLoading(true);
      return;
    }

    // High-performance listener: We show all firm documents in the administrative queue.
    // Filtering by assigned staff is handled via UI filters for better performance
    // and to avoid blocking the main thread with hundreds of individual 'getDoc' calls.
    const unsub = subscribeToAllFirmDocuments((allDocs) => {
      // Filter out completed documents from the active review queue
      const activeDocs = allDocs.filter(doc => 
        doc.status !== 'APPROVED' && 
        doc.status !== 'ADDED_TO_RETURN'
      );

      // Sort: Pending status first, then newest uploads first
      const sorted = [...activeDocs].sort((a, b) => {
        if (a.status === 'PENDING' && b.status !== 'PENDING') return -1;
        if (a.status !== 'PENDING' && b.status === 'PENDING') return 1;
        return new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime();
      });

      setDocuments(sorted);
      setLoading(false);
    });

    return () => unsub();
  }, [user?.uid, userDoc?.role, firmId]);

  return { documents, loading };
};
